import React, { Fragment, useState, useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { FormattedMessage } from 'react-intl';
import withStyles from '@material-ui/core/styles/withStyles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import Hidden from '@material-ui/core/Hidden';
import IconButton from '@material-ui/core/IconButton';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import ceil from 'lodash/ceil';
import qs from 'query-string';

import messages from 'aurora-translations';
import history from 'aurora-utils/history';
import { useDeepEffect } from 'aurora-hooks';

import { CleverTapPropertiesFilter } from 'aurora-utils/cleverTap.eventhelper';
import {
  fetchBuyerList,
  uploadDocument,
  closeBuyerModal,
  deleteDocument,
  addBuyer,
} from 'aurora-composition/modules/buyersList/actions';
import Box from '@material-ui/core/Box';
import { fetchWorkFlowApplication } from 'aurora-composition/modules/offPlanTransaction/actions';
import ADGMBreadcrumbs from '../ADGMBreadcrumbs';
import Filters from './Filters';
import ADGMPagination from '../ADGMPagination';
import ADGMSnackbar from '../ADGMSnackbar';
import NoBuyerList from './NoBuyerList';
import Loading from '../Loading';
import BuyerList from './BuyerList';
import styles from './styles';
import AddBuyerModal from './AddBuyerModal';
import BuyerFields from './BuyerFields';

const PAGE_SIZE = 10;

function ADGMBuyersList(props) {
  const {
    classes,
    breadCrumbs,
    hideTitle,
    hideFilters,
    hiddenFilters,
    hidePropertyTitle,
    hidePropertyToggle,
    selectable,
    onNext,
    defaultFilters,
    defaultSelected,
    state: routeState,
    goBack,
  } = props;
  const {
    location: { search },
  } = history;
  const [selected, setSelected] = useState(defaultSelected);
  const query = qs.parse(search);
  const { page = 1, ...filters } = query;

  const {
    buyerList,
    buyerSelected,
    isExpired,
    addBuyerModal,
    documents,
    loading,
    error,
    locale,
    nationalities,
  } = useSelector(
    createSelector(
      state => state.buyersListReducer,
      property => ({
        buyerList: property.buyerList,
        buyerSelected: property.buyerSelected,
        isExpired: property.isExpired,
        addBuyerModal: property.addBuyerModal,
        documents: property.documents,
        loading: property.loading,
        error: property.error,
        locale: property.locale,
        nationalities: property.nationalities,
      }),
    ),
  );

  const { applicationId, applicationType } = useSelector(
    createSelector(
      state => state.offPlanReducer,
      property => ({
        applicationId: property.applicationId,
        applicationType: property.application?.applicationType,
      }),
    ),
  );
  const dispatch = useDispatch();

  useDeepEffect(() => {
    const payload = { page, PAGE_SIZE, filters, defaultFilters };
    if (!isEmpty(filters)) {
      dispatch(fetchBuyerList(payload));
    }
  }, [search, filters]);

  const handleQueryChange = useCallback(
    newQuery => {
      history.replace({
        search: qs.stringify({ id: query.id, ...newQuery }),
        state: { ...routeState },
      });
      CleverTapPropertiesFilter(newQuery);
    },
    [search],
  );

  const handlePageChange = useCallback(
    num => {
      const newQuery = { ...query, page: num };
      handleQueryChange(newQuery);
    },
    [query],
  );

  const handleNext = useCallback(() => {
    onNext && onNext(selected);
  }, [selected]);

  const onNextDisabled = useMemo(() => {
    if (loading || isEmpty(selected) || isEmpty(buyerList)) return true;
    return false;
  }, [selected, loading]);

  const handleDocumentUpload = (body, params) => {
    dispatch(uploadDocument(body, params));
  };

  const handleModalClose = () => {
    dispatch(closeBuyerModal());
  };

  const handleDocumentRemove = useCallback(
    subType => {
      const params = {
        applicationId,
        applicationType,
        documentType: subType?.type,
        subType: subType?.subType,
      };
      dispatch(deleteDocument(params, subType));
    },
    [documents],
  );

  const handleFileRemove = subType => {
    handleDocumentRemove(subType);
  };

  const handleFileUpload = event => {
    const { name, files } = event.target;
    if (!files[0]) return;
    const fileIndex =
      documents.filter(doc => doc.subType?.includes(`NON_RESIDENT_BUYER_${buyerSelected?.userId}.${name}_`))?.length +
        1 || 1;
    // NON_RESIDENT_

    const params = {
      applicationId,
      documentType: name,
      applicationType,
      subType: `NON_RESIDENT_BUYER_${buyerSelected?.userId}.${name}_${fileIndex}`,
      userId: buyerSelected?.userId,
    };
    const formData = new FormData();
    formData.append('file', files[0]);
    handleDocumentUpload && handleDocumentUpload(formData, params);
  };

  const handleAddBuyer = async () => {
    const res = await dispatch(addBuyer(buyerSelected));
    if (res.success) {
      goBack();
      dispatch(fetchWorkFlowApplication(applicationId));
    }
  };

  return (
    <Fragment>
      {error && isString(error) && (
        <ADGMSnackbar message={messages[error]?.id ? <FormattedMessage {...messages[error]} /> : error} />
      )}
      {loading && <Loading />}

      {/* Breadcrumbs */}
      <Hidden smDown>
        <ADGMBreadcrumbs paths={breadCrumbs} />
      </Hidden>
      <Hidden mdUp>
        <div className={classes.mobileTitleHead}>
          <IconButton aria-label='back' onClick={() => goBack()}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant='h6' component='h2'>
            <FormattedMessage {...messages.select_properties} />
          </Typography>
        </div>
      </Hidden>

      {/* Filters */}
      <Filters
        query={query}
        handleQueryChange={handleQueryChange}
        locale={locale}
        loading={loading}
        hideTitle={hideTitle}
        hideFilters={hideFilters}
        hiddenFilters={hiddenFilters}
        hidePropertyTitle={hidePropertyTitle}
        hidePropertyToggle={hidePropertyToggle}
        classes={classes}
        nationalities={nationalities}
      />
      {/* ! */}
      {!isEmpty(filters) && (
        <>
          {/* Property List */}
          <Box component='hr' mt={2} mb={4} className={classes.hr} />
          {!loading && isEmpty(buyerList) && <NoBuyerList />}
          {!isEmpty(buyerList) && (
            <BuyerList
              locale={locale}
              selectable={selectable}
              selected={selected}
              setSelected={setSelected}
              page={page}
              history={history}
              data={buyerList}
              isExpired={isExpired}
              buyerSelected={buyerSelected}
              nationalities={nationalities}
            />
          )}

          {/* Pagination */}
          {!isEmpty(buyerList) && (
            <div className={classes.paginationHolder}>
              <ADGMPagination
                // totalPages={ceil(buyerList?.totalCount / PAGE_SIZE)}
                totalPages={ceil(buyerList?.length / PAGE_SIZE)}
                currentPage={Number(page)}
                onPageChange={handlePageChange}
                customClasses={{ root: classes.pagination }}
              />
            </div>
          )}

          {/* Next button footer */}
          {selectable && (
            <div className={classes.footer}>
              <Container className={classes.container}>
                <Button variant='outlined' onClick={() => goBack()} className={classes.cancelBtn} size='large'>
                  <FormattedMessage {...messages.cancel} />
                </Button>
                <div>
                  <Button
                    color='primary'
                    variant='contained'
                    className={classes.proceedBtn}
                    onClick={handleNext}
                    disabled={onNextDisabled}
                  >
                    <Hidden xsDown>
                      <FormattedMessage {...messages.proceed} />
                    </Hidden>
                    <Hidden smUp>
                      <FormattedMessage
                        {...messages.selected_properties_count}
                        values={{
                          count: selected?.length || 0,
                        }}
                      />
                    </Hidden>
                  </Button>
                </div>
              </Container>
            </div>
          )}
        </>
      )}

      {/* ADD BUYER MODAL */}
      <AddBuyerModal
        buyerSelected={buyerSelected}
        addBuyerModal={addBuyerModal}
        handleModalClose={handleModalClose}
        classes={classes}
        locale={locale}
        documents={documents}
        modalTitle={<FormattedMessage {...messages.Add_buyer} />}
        userDetailsTitle={<FormattedMessage {...messages.SP_ONLINE_BUYER_DETAILS} />}
        userDocsTitle={<FormattedMessage {...messages.Buyer_documents} />}
        buttonText={<FormattedMessage {...messages.Add_buyer} />}
        handleAddUser={handleAddBuyer}
        handleFileUpload={handleFileUpload}
        handleFileRemove={handleFileRemove}
        FieldComponent={BuyerFields}
      />
    </Fragment>
  );
}

ADGMBuyersList.propTypes = {
  classes: PropTypes.object.isRequired,
  breadCrumbs: PropTypes.array.isRequired,
  goBack: PropTypes.func.isRequired,
  hideTitle: PropTypes.bool,
  hideFilters: PropTypes.bool,
  hidePropertyTitle: PropTypes.bool,
  hidePropertyToggle: PropTypes.bool,
  hiddenFilters: PropTypes.array,
  selectable: PropTypes.bool,
  onNext: PropTypes.func,
  defaultFilters: PropTypes.object,
  defaultSelected: PropTypes.object,
  state: PropTypes.object,
};

ADGMBuyersList.defaultProps = {
  hideTitle: false,
  hideFilters: false,
  hidePropertyToggle: false,
  hidePropertyTitle: false,
  hiddenFilters: [],
  selectable: false,
  onNext: null,
  defaultFilters: {},
  defaultSelected: null,
  state: {},
};

export default withStyles(styles)(ADGMBuyersList);
